import { useEffect, useState } from "react";
import {
  Modal,
  Select,
  Textarea,
  TextInput,
  Button,
  Group,
  Stack,
} from "@mantine/core";
import { usersApi } from "../../api/users";
import { driversApi } from "../../api/drivers";
import {
  EntitySearchSelect,
  type EntityOption,
} from "../../components/common/EntitySearchSelect";
import { humanize } from "../../lib/labels";

const QUERY_TYPES = [
  "driverRelated",
  "fareRelated",
  "paymentRelated",
  "tripRelated",
  "bookingRelated",
  "accountRelated",
  "supportRelated",
  "other",
];

function label(name?: string, phone?: string) {
  return [name, phone].filter(Boolean).join(" · ") || "Unknown";
}

async function searchUsers(search: string): Promise<EntityOption[]> {
  const r = await usersApi.list({ search, page: 1, limit: 10 });
  return r.items.map((u) => ({
    value: u._id,
    label: label(u.personalDetails?.name, u.personalDetails?.phoneNumber),
  }));
}
async function searchDrivers(search: string): Promise<EntityOption[]> {
  const r = await driversApi.list({ search, page: 1, limit: 10 });
  return r.items.map((d) => ({
    value: d._id,
    label: label(d.personalDetails?.name, d.personalDetails?.phoneNumber),
  }));
}

export interface QueryCreateBody {
  queryType: string;
  message: string;
  createdByInfo: { userType: string; createdBy: string };
  tripID?: string;
}

export function QueryFormModal({
  opened,
  onClose,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: (body: QueryCreateBody) => void;
}) {
  const [queryType, setQueryType] = useState("other");
  const [message, setMessage] = useState("");
  const [userType, setUserType] = useState("passenger");
  const [createdBy, setCreatedBy] = useState("");
  const [tripID, setTripID] = useState("");

  useEffect(() => {
    if (opened) {
      setQueryType("other");
      setMessage("");
      setUserType("passenger");
      setCreatedBy("");
      setTripID("");
    }
  }, [opened]);

  const valid = message.trim().length > 0 && createdBy;

  return (
    <Modal opened={opened} onClose={onClose} title="New support query" centered size="lg">
      <Stack>
        <Select
          label="Type"
          data={QUERY_TYPES.map((t) => ({ value: t, label: humanize(t) }))}
          value={queryType}
          onChange={(v) => setQueryType(v ?? "other")}
          allowDeselect={false}
        />
        <Select
          label="Raised by"
          data={[
            { value: "passenger", label: "Rider" },
            { value: "driver", label: "Driver" },
          ]}
          value={userType}
          onChange={(v) => {
            setUserType(v ?? "passenger");
            setCreatedBy("");
          }}
          allowDeselect={false}
        />
        <EntitySearchSelect
          label={userType === "driver" ? "Driver" : "Rider"}
          value={createdBy}
          onChange={setCreatedBy}
          queryKey={userType}
          fetcher={userType === "driver" ? searchDrivers : searchUsers}
          required
        />
        <Textarea
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          minRows={3}
          autosize
          required
        />
        <TextInput
          label="Trip ID (optional)"
          placeholder="Link this query to a trip"
          value={tripID}
          onChange={(e) => setTripID(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() =>
              onSubmit({
                queryType,
                message: message.trim(),
                createdByInfo: { userType, createdBy },
                ...(tripID.trim() ? { tripID: tripID.trim() } : {}),
              })
            }
          >
            Create query
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
